import App from '@app/app';
import type { SeasonDTO, LookAllowDTO, LookAllowlists } from '@app/service/season/season.interface';
import type { LayoutThemeID, SkuCode, SkuSet } from '@app/service/common/common.interface';
import { MAX_DATE, MIN_DATE } from '@app/utils/dates';
import type { IJobStore, Job } from '@app/interface/job/job-store.interface';

export class LookAllowlistService {
  constructor(private readonly jobStore: IJobStore) {}

  public getWhitelist(skuCode: SkuCode, jobKey?: string): Set<LayoutThemeID> | null {
    const job = this.jobStore.getJob(jobKey) as Job;

    return this.getWhitelistByJob(skuCode, job);
  }

  public getWhitelistByJob(skuCode: SkuCode, job: Job): Set<LayoutThemeID> | null {
    return job.season?.lookWhitelist[skuCode] ?? null;
  }

  public getBlacklist(jobKey?: string): Set<LayoutThemeID> | null {
    return this.jobStore.getJob(jobKey).season?.lookBlacklist ?? null;
  }

  public getAllowlists(season: SeasonDTO, skuSets: Record<string, SkuSet>): LookAllowlists {
    return {
      lookWhitelist: this.buildWhitelist(season.lookWhitelists ?? [], skuSets),
      lookBlacklist: this.buildBlacklist(season.seasonalDidSetups ?? [])
    };
  }

  private buildWhitelist(
    whitelists: LookAllowDTO[],
    skuSets: Record<string, SkuSet>
  ): Record<SkuCode, Set<LayoutThemeID>> {
    return whitelists.reduce(
      (acc, whitelist) => {
        const isWithinTimeRange = this.isWithinTimeRange(whitelist);

        const skus = whitelist.skuSets.flatMap((skuSet) => skuSets[skuSet.toUpperCase()].skus);

        skus.forEach((sku: SkuCode) => {
          const layoutThemeIDs = (acc[sku] ??= new Set<LayoutThemeID>());

          acc[sku] = isWithinTimeRange ? this.merge(layoutThemeIDs, whitelist.layoutThemeIDs) : layoutThemeIDs;
        });

        return acc;
      },
      {} as Record<SkuCode, Set<LayoutThemeID>>
    );
  }

  private buildBlacklist(blacklists: LookAllowDTO[]): Set<LayoutThemeID> {
    return blacklists.reduce(
      (layoutThemeIDs, blacklist) =>
        this.isWithinTimeRange(blacklist) ? layoutThemeIDs : this.merge(layoutThemeIDs, blacklist.layoutThemeIDs),
      new Set<LayoutThemeID>()
    );
  }

  private isWithinTimeRange(allowlist: LookAllowDTO): boolean {
    const now = Date.now();

    const startTime = allowlist.startTime ? Date.parse(allowlist.startTime) : MIN_DATE.getTime();
    const endTime = allowlist.endTime ? Date.parse(allowlist.endTime) : MAX_DATE.getTime();

    return now >= startTime && now <= endTime;
  }

  private merge(set: Set<LayoutThemeID>, arr: LayoutThemeID[]) {
    arr.forEach((layoutThemeID) => set.add(layoutThemeID));

    return set;
  }
}

App.service('LookAllowlistService', ['JobStore', LookAllowlistService]);
